import { useTranslation } from 'react-i18next';
import React, { useCallback, useMemo } from 'react';
import { Input } from 'components/ui/input';
import { RenderedHtml } from 'modules/editor';

interface BlankSlot {
  id: string;
  position: number;
  acceptedAnswers?: string[];
}

interface FillInBlanksPlayerProps {
  prompt: string;
  blanks: BlankSlot[];
  value: Record<string, string>;
  onChange: (value: Record<string, string>) => void;
  disabled?: boolean;
}

/** Replaces the prompt's `{{blank_id}}` placeholders with inline inputs. */
export function FillInBlanksPlayer({
  prompt,
  blanks,
  value,
  onChange,
  disabled = false,
}: FillInBlanksPlayerProps) {
  const { t } = useTranslation();
  const handleBlankChange = useCallback(
    (blankId: string, inputValue: string) => {
      onChange({
        ...value,
        [blankId]: inputValue,
      });
    },
    [value, onChange],
  );

  // Parse prompt and replace blank placeholders with input fields
  const renderedContent = useMemo(() => {
    // Split by blank placeholders: {{blank_id}}
    const parts = prompt.split(/(\{\{[^}]+\}\})/g);

    return parts.map((part, index) => {
      const blankMatch = part.match(/\{\{([^}]+)\}\}/);
      if (blankMatch) {
        const blankId = blankMatch[1];
        const blank = blanks.find((b) => b.id === blankId);
        if (blank) {
          return (
            <Input
              key={`blank-${blankId}-${index}`}
              type="text"
              aria-label={`${t('reading_blank')} ${blanks.indexOf(blank) + 1}`}
              autoComplete="off"
              value={value[blankId] || ''}
              onChange={(e) => handleBlankChange(blankId, e.target.value)}
              disabled={disabled}
              className="mx-1 inline-block h-8 w-32 text-center text-sm"
              placeholder="..."
            />
          );
        }
      }
      // Each segment renders through the shared HTML/KaTeX pipeline, so math
      // between blanks typesets like it does everywhere else.
      return <RenderedHtml key={`text-${index}`} html={part} inline />;
    });
  }, [prompt, blanks, value, handleBlankChange, disabled, t]);

  return (
    <div className="fill-in-blanks-player">
      <div className="text-sm leading-relaxed text-gray-900">{renderedContent}</div>
      <p className="mt-3 text-xs text-gray-500">
        {t('reading_fill_hint')}
      </p>
    </div>
  );
}

export default FillInBlanksPlayer;
